import React from "react";
import { Snackbar, Alert } from "@mui/material";

export type SnackbarSeverity = "success" | "error" | "info" | "warning";

interface SnackbarState {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
}

interface UseSnackbarOptions {
  duration?: number;
  anchorOrigin?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
}

interface UseSnackbarReturn {
  /** Show a snackbar with the given message and optional severity (default: "success"). */
  showSnackbar: (message: string, severity?: SnackbarSeverity) => void;
  /** Dismiss the active snackbar. */
  hideSnackbar: () => void;
  /** Raw snackbar state — use this to render an inline Alert instead of a floating Snackbar. */
  snackbar: SnackbarState;
  /** Ready-to-render floating Snackbar. Place this anywhere in your JSX tree. */
  SnackbarNode: React.ReactNode;
}

/**
 * Universal snackbar hook. Eliminates the repeated snackbar state + JSX pattern.
 *
 * Usage (floating snackbar):
 *   const { showSnackbar, SnackbarNode } = useSnackbar();
 *   showSnackbar("Saved!", "success");
 *   return <>{content}{SnackbarNode}</>;
 *
 * Usage (inline Alert, e.g. inside a dialog):
 *   const { showSnackbar, hideSnackbar, snackbar } = useSnackbar();
 *   return snackbar.open
 *     ? <Alert severity={snackbar.severity} onClose={hideSnackbar}>{snackbar.message}</Alert>
 *     : null;
 */
export function useSnackbar(options?: UseSnackbarOptions): UseSnackbarReturn {
  const duration = options?.duration ?? 6000;
  const anchorOrigin = options?.anchorOrigin ?? {
    vertical: "bottom",
    horizontal: "right",
  };

  const [snackbar, setSnackbar] = React.useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = React.useCallback(
    (message: string, severity: SnackbarSeverity = "success") => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  const hideSnackbar = React.useCallback(() => {
    setSnackbar((s) => ({ ...s, open: false }));
  }, []);

  const SnackbarNode = (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={duration}
      onClose={hideSnackbar}
      anchorOrigin={anchorOrigin}
    >
      <Alert onClose={hideSnackbar} severity={snackbar.severity}>
        {snackbar.message}
      </Alert>
    </Snackbar>
  );

  return { showSnackbar, hideSnackbar, snackbar, SnackbarNode };
}
